import { Tabs as BaseTabs } from '@base-ui/react/tabs'
import clsx from 'clsx'
import type { ReactNode } from 'react'

interface TabsRootProps<T extends string> {
  value: T
  onChange: (next: T) => void
  className?: string
  children: ReactNode
}

function TabsRoot<T extends string>({ value, onChange, className, children }: TabsRootProps<T>) {
  return (
    <BaseTabs.Root
      value={value}
      onValueChange={(next) => onChange(next as T)}
      className={className}
    >
      {children}
    </BaseTabs.Root>
  )
}

interface TabsListProps {
  ariaLabel?: string
  className?: string
  children: ReactNode
}

function TabsList({ ariaLabel, className, children }: TabsListProps) {
  return (
    <BaseTabs.List
      aria-label={ariaLabel}
      className={clsx('relative flex gap-6 border-b border-gray-stroke', className)}
    >
      {children}
      <BaseTabs.Indicator
        className={clsx(
          'absolute bottom-[-1px] h-[3px] bg-charcoal-75',
          'left-[var(--active-tab-left)] w-[var(--active-tab-width)]',
          'transition-[left,width] duration-200 ease-out'
        )}
      />
    </BaseTabs.List>
  )
}

interface TabsTabProps {
  value: string
  count?: number
  children: ReactNode
}

function TabsTab({ value, count, children }: TabsTabProps) {
  return (
    <BaseTabs.Tab
      value={value}
      className={clsx(
        'flex items-center gap-2 px-1 py-2 text-sm text-charcoal-50 transition-colors hover:text-charcoal',
        'data-[selected]:font-semibold data-[selected]:text-charcoal'
      )}
    >
      <span>{children}</span>
      {count !== undefined && (
        <span className="inline-flex items-center rounded-full bg-sand-75 px-2 text-sm text-charcoal-75">
          {count}
        </span>
      )}
    </BaseTabs.Tab>
  )
}

interface TabsPanelProps {
  value: string
  className?: string
  children: ReactNode
}

function TabsPanel({ value, className, children }: TabsPanelProps) {
  return (
    <BaseTabs.Panel value={value} className={className}>
      {children}
    </BaseTabs.Panel>
  )
}

export const Tabs = Object.assign(TabsRoot, {
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel,
})
